import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import GlobalDeleteModal from "../../components/partials/GlobalDeleteModal";
import BlogApi from "../../api/BlogApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import { useLoader } from "../../utils/LoaderUtils";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import TableShimmer from "../../components/partials/TableShimmer";
import blogsAPI from "../../api/BlogApi";
import useQueryFilters from "../../hooks/useQueryFilters";
import useDeleteItem from "../../hooks/useDeleteItem";
import SortableRow from "./SortableRow";
import Pagination from "../../components/partials/Pagination";
import RowsPerPage from "../../components/partials/RowsPerPage";

function List() {
  const { showLoader, hideLoader } = useLoader();
  const [tableLoading, setTableLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({});

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  );

  /* =============  FETCH API DATA ================ */
  const { query, updateFilter, handleSearch, searchParams, searchMessage } =
    useQueryFilters();

  const fetchBlogData = useCallback(async () => {
    setTableLoading(true);
    setItems([]);

    try {
      const res = await BlogApi.getDetails(searchParams);

      if (res.code === 200) {
        const result = res.data;

        setItems(result.blogs);
        setPagination(result.pagination);
      }
    } catch (err) {
    } finally {
      setTableLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchBlogData();
  }, [fetchBlogData]);

  const { deleteId, isDeleting, openDelete, handleDelete } = useDeleteItem(
    BlogApi.deleteItem,
    fetchBlogData,
  );

  /* =============  STATUS CHANGE API ================ */
  const handleStatusChange = useCallback(async (id, status) => {
    showLoader();

    try {
      const res = await blogsAPI.updateStatus(id);

      if (res.code === 200) {
        hideLoader();
        showSuccess("Status updated successfully");

        setItems((prev) =>
          prev.map((item) => (item._id === id ? { ...item, status } : item)),
        );
      } else {
        showError("Failed to update status");
      }
    } catch (err) {
      hideLoader();
      showError("Something went wrong");
    }
  }, []);

  /* =============  REORDER API ================ */
  const handleDragEnd = useCallback(
    async (event) => {
      const { active, over } = event;
      if (active.id !== over.id) {
        const oldIndex = items.findIndex((i) => i._id === active.id);
        const newIndex = items.findIndex((i) => i._id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        setItems(newItems);

        const updatedOrder = newItems.map((item, index) => ({
          id: item._id,
          sort_order: index + 1,
        }));

        try {
          const res = await BlogApi.updateOrder(updatedOrder);
          if (res.code === 200) {
            showSuccess(res.message);
          }
        } catch (error) {
          console.error("Order update failed", error);
        }
      }
    },
    [items],
  );

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Manage Blogs</h4>
              <h6>View, add and edit Blogs displayed across your store.</h6>
            </div>
          </div>
          <div className="page-btn">
            <Link to="/blog/add-new" className="btn btn-primary">
              <i className="ti ti-circle-plus me-1" />
              Add Blog
            </Link>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="row">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text" id="basic-addon1">
                    <i className="ti ti-search" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search By Store Name/Email Address/Phone Number"
                    defaultValue={query.search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>
                {searchMessage && (
                  <small className="text-danger">{searchMessage}</small>
                )}
              </div>
              <div className="col-md-6" style={{ justifyItems: "right" }}>
                <div className="d-flex table-dropdown my-xl-auto right-content align-items-center row-gap-3">
                  <select
                    className="form-control ms-2"
                    value={searchParams.get("status") || ""}
                    onChange={(e) => updateFilter("status", e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="row">
              <div className="col-md-12">
                <div className="countablepagination p-2">
                  {pagination?.total > 0 && (
                    <p>
                      Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                      {Math.min(
                        pagination.page * pagination.limit,
                        pagination.total,
                      )}{" "}
                      of {pagination.total} results
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <table className="table w-100">
                  <thead className="thead-light">
                    <tr>
                      <th>S. No.</th>
                      <th>Blog Details</th>
                      <th>Created At</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableLoading ? (
                      <TableShimmer rows={10} cols={4} />
                    ) : items.length > 0 ? (
                      <SortableContext
                        items={items.map((i) => i._id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {items.map((item, index) => (
                          <SortableRow
                            key={item._id}
                            item={item}
                            index={index}
                            page={pagination?.page}
                            limit={pagination?.limit}
                            handleStatusChange={handleStatusChange}
                            openDelete={openDelete}
                          />
                        ))}
                      </SortableContext>
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center p-4 text-muted">
                          No Blog Added,{" "}
                          <Link to="/blog/add-new" className="text-primary">
                            Click Here{" "}
                          </Link>
                          to add new blog
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </DndContext>
            </div>

            <div className="row">
              <RowsPerPage
                value={searchParams.get("limit") || "10"}
                onChange={(val) => updateFilter("limit", val)}
              />

              <Pagination pagination={pagination} updateFilter={updateFilter} />
            </div>
          </div>
        </div>
      </div>

      <GlobalDeleteModal onDeleted={handleDelete} isDeleting={isDeleting} />
    </>
  );
}

export default List;
